/**
 * Marking one navigation as a handoff, so the conversation follows it.
 *
 * Opening a model does not reopen whatever was last said about it. Somebody coming to
 * a model wants to ask about the model, not to be dropped back into the middle of a
 * conversation they may have finished days ago, so previous sessions are offered
 * rather than resumed.
 *
 * A reload is not an arrival. It is the same page again, so the conversation that was
 * open comes back (`rememberOpenChat` below), and this rule is not about it.
 *
 * A duplication is the exception, and the only one. The agent copies a live model, the
 * work continues on the copy and the page is taken there, so arriving to an empty
 * panel would strand every decision on the other side of a navigation the app made by
 * itself. That case is deliberate, this is how it says so, and nothing else sets it.
 *
 * `sessionStorage`, one key: it describes a single navigation rather than remembering
 * anything. Every access is wrapped, because storage throws in a private window and a
 * chat panel must not fail to render over it; losing the mark degrades to offering the
 * session instead of resuming it, which is the ordinary behaviour rather than a broken
 * one.
 */

const KEY = 'mk-studio-chat-handoff';

/** Say that the next arrival at `modelId` is a handoff, not an ordinary visit. */
export function markHandoff(modelId: string): void {
  if (!modelId) return;
  try {
    window.sessionStorage.setItem(KEY, String(modelId));
  } catch {
    // No storage: the copy offers the conversation rather than reopening it.
  }
}

/**
 * Whether arriving at `modelId` is that handoff.
 *
 * **`spend` is whether this arrival is the last one, and it is not always.** The copy's
 * own page reloads itself the moment the duplication finishes, because the dataset
 * metadata behind it is resolved server side (`RootPageModel.getDuplicationStatus`). So
 * a page landed on mid-duplication arrives TWICE, and a mark taken by the first arrival
 * is gone by the second: the conversation appeared, and then emptied itself at exactly
 * the moment the user was watching for the copy to be ready. Pass `spend: false` while a
 * reload is still coming and the mark survives it.
 *
 * A mark naming a DIFFERENT model is discarded whatever `spend` says. That one is stale
 * by definition, because somebody is looking somewhere else, and leaving it would reopen
 * a conversation somewhere unrelated later in the tab.
 *
 * Effects only. This panel is server-rendered and `sessionStorage` does not exist
 * there, so reading during render would crash the page rather than degrade.
 */
export function claimHandoff(
  modelId: string,
  { spend = true }: { spend?: boolean } = {}
): boolean {
  try {
    const marked = window.sessionStorage.getItem(KEY);
    if (marked === null) return false;
    const mine = String(marked) === String(modelId);
    if (!mine || spend) window.sessionStorage.removeItem(KEY);
    return mine;
  } catch {
    return false;
  }
}

const OPEN_KEY = 'mk-studio-chat-open';

/**
 * The conversation open on `modelId` right now, so a reload of this tab reopens it.
 *
 * The panel clears it when it unmounts, which leaving the model inside the app does and
 * a reload does not: the page is gone before any cleanup runs. So what survives to the
 * next mount is exactly "this page, reloaded", and an arrival from anywhere else finds
 * nothing and is offered sessions as before. Same storage and the same wrapping as the
 * handoff mark above, for the same reasons.
 */
export function rememberOpenChat(modelId: string, threadId: string): void {
  if (!modelId || !threadId) return;
  try {
    window.sessionStorage.setItem(
      OPEN_KEY,
      JSON.stringify({ model: String(modelId), thread: threadId })
    );
  } catch {
    // No storage: a reload offers the conversation instead of reopening it.
  }
}

/** The panel left this conversation, or the model: a reload has nothing to reopen. */
export function forgetOpenChat(): void {
  try {
    window.sessionStorage.removeItem(OPEN_KEY);
  } catch {
    // Nothing stored, nothing to clear.
  }
}

/** The thread that was open on `modelId` before this page reloaded, or null. */
export function recallOpenChat(modelId: string): string | null {
  try {
    const raw = window.sessionStorage.getItem(OPEN_KEY);
    if (!raw) return null;
    const open = JSON.parse(raw) as { model?: string; thread?: string };
    return open.model === String(modelId) && open.thread ? open.thread : null;
  } catch {
    return null;
  }
}
